import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { BooleanInput } from '@angular/cdk/coercion';
import { Subject, takeUntil } from 'rxjs';
import { User } from 'app/core/user/user.types';
import { UserService } from 'app/core/user/user.service';
import { ChangePasswordComponent } from '../change-password/change-password.component';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from 'app/services/auth/auth.service';
import { SharedService } from 'app/services/shared.service';
import { TranslateService } from '@ngx-translate/core';
import { SesionService } from 'app/services/sesion.service';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user'
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */
    userImage: string = "assets/images/avatars/usuario.png";
    @Input() showAvatar: boolean = true;
    user: User;

    // Propiedad para almacenar el idioma actual
    currentLang: string;

    // Mapeo de códigos de idioma a IDs
    private idiomaMap = {
        'es': 0,
        'en': 1
    };

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        public dialog: MatDialog,
        private _auth: AuthService,
        public shared: SharedService,
        private translate: TranslateService,
        private _sesion: SesionService
    ) {
        this.initializeLanguage();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void {
        // Return if user is not available
        if (!this.user) {
            return;
        }

        // Update the user
        this._userService.update({
            ...this.user,
            status
        }).subscribe();
    }

    initializeLanguage() {
        // 1. Intentar obtener el idioma guardado
        const savedLang = localStorage.getItem('lang');
        this.translate.setDefaultLang('es'); // Establecer 'es' como idioma principal

        let finalLang: string;

        if (savedLang) {
            // Caso 1: Usar el idioma guardado
            finalLang = savedLang;
        } else {
            // Caso 2: Usar el idioma del navegador si es 'es' o 'en'
            const browserLang = this.translate.getBrowserLang();
            finalLang =
                browserLang && browserLang.match(/es|en/) ? browserLang : 'es';
        }

        this.translate.use(finalLang);
        this.currentLang = finalLang; // Sincroniza la variable con el idioma usado
    }


    cambiarIdioma(language: string) {
        console.log('=== cambiarIdioma llamado con:', language);
        this.translate.use(language);
        this.currentLang = language;
        localStorage.setItem('lang', language);
        
        // Actualizar el idioma en el backend
        this.updateIdiomaBackend(language);
    }

    /**
     * Actualiza el idioma en el backend
     */
    private updateIdiomaBackend(lang: string): void {
        console.log('=== updateIdiomaBackend llamado con:', lang);
        console.log('datosUser:', this.shared.datosUser);
        
        // Verificar que el usuario esté autenticado
        if (this.shared.isNullOrUndefined(this.shared.datosUser)) {
            console.warn('Usuario no autenticado, no se actualiza idioma en backend');
            return;
        }

        // Obtener el idiomaId correspondiente
        const idiomaId = this.idiomaMap[lang];
        
        console.log('idiomaId obtenido del mapa:', idiomaId);
        
        if (idiomaId === undefined || idiomaId === null) {
            console.warn(`Idioma no mapeado: ${lang}`);
            return;
        }

        console.log('=== Preparando request al backend con idiomaId:', idiomaId);

        // Crear el FormData
        const form = new FormData();
        form.append('idiomaId', `${idiomaId}`);
        
        // Incluir otros campos requeridos del perfil
        if (this.shared.datosUser.nombre) {
            form.append('nombre', this.shared.datosUser.nombre);
        }
        if (this.shared.datosUser.apellidoPaterno) {
            form.append('apellidoPaterno', this.shared.datosUser.apellidoPaterno);
        }
        if (this.shared.datosUser.apellidoMaterno) {
            form.append('apellidoMaterno', this.shared.datosUser.apellidoMaterno);
        }
        if (this.shared.datosUser.telefono) {
            form.append('telefono', this.shared.datosUser.telefono);
        }
        if (this.shared.datosUser.ubicacionId) {
            form.append('ubicacionId', `${this.shared.datosUser.ubicacionId}`);
        }
        form.append('borrarImagen', 'false');

        console.log('=== Enviando request al backend...');

        // Hacer la llamada al backend
        this._sesion.saveWeb(form).subscribe({
            next: (data) => {
                console.log('=== Respuesta del backend:', data);
                if (data.isSuccess) {
                    this.shared.datosUser.idiomaId = idiomaId;
                    console.log('✅ Idioma actualizado en el backend correctamente');
                } else {
                    console.error('❌ Error al actualizar idioma:', data.msg);
                }
            },
            error: (error) => {
                console.error('❌ Error al actualizar idioma en el backend:', error);
            }
        });
    }

    /**
     * Sign out
     */
    signOut(): void {
        // this._router.navigate(['/sign-out']);
        this._auth.signOut();
    }


    openDialogChangePassword(): void {
        const dialogRef = this.dialog.open(ChangePasswordComponent, {
            width: '400px',
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
            console.log('The dialog was closed');
        });
    }

}
